import { getUserSelectedCameraDeviceId } from '../settings/functions.web';
import { shortFingerprint } from '../util/shortFingerprint';
import { cameraErrorDetails, describeObtainedCamera } from './cameraDiagnostics.web';
import { notifyCameraSubstituted, roomDispatch } from './cameraSubstitutionNotice.web';
import { getMediaErrorName } from './mediaErrors';
/**
 * Device-level failures that another camera can get past. A permission
 * refusal applies to every camera, so it is never worked around here.
 */
const OTHER_CAMERA_ERRORS = new Set(['AbortError', 'NotFoundError', 'NotReadableError', 'OverconstrainedError']);
/** Bounds the extra getUserMedia calls a machine with many cameras can cost. */
const MAX_OTHER_CAMERAS = 3;
/**
 * The preferred camera could not start: busy in another browser on the same
 * machine, unplugged, or a saved deviceId that no longer exists. Another
 * camera may be free, so try each one once before joining without video.
 *
 * Candidates come from the raw browser enumeration, not the deduplicated
 * picker list, because two identical USB cameras can report one groupId.
 *
 * @returns {Promise<Array>} The tracks from the first camera that starts.
 * @throws The original failure when no other camera starts.
 */
export async function acquireOtherCamera({ createVideoTrack, emit, enumerateDevices = () => navigator.mediaDevices.enumerateDevices(), failure, getState, isCurrent, notify = substitution => notifyCameraSubstituted(roomDispatch(), substitution), triedDeviceId }) {
    const failureName = getMediaErrorName(failure);
    if (!OTHER_CAMERA_ERRORS.has(failureName)) {
        throw failure;
    }
    const preferredDeviceId = triedDeviceId || getUserSelectedCameraDeviceId(getState());
    // Every outcome says which camera failed (hashed) and how, in the
    // browser's words; failure_name alone cannot tell a busy camera from a
    // stale id.
    const report = {
        ...cameraErrorDetails(failure),
        failure_name: failureName,
        requestedCameraRef: preferredDeviceId ? shortFingerprint(preferredDeviceId) : undefined
    };
    let devices = [];
    try {
        devices = await enumerateDevices();
    }
    catch {
        emit('camera_other_camera_fallback', {
            ...report,
            candidates: 0,
            outcome: 'enumeration_failed'
        });
        throw failure;
    }
    const candidates = [...new Set(devices
            .filter(device => device.kind === 'videoinput' && device.deviceId && device.deviceId !== preferredDeviceId)
            .map(device => device.deviceId))].slice(0, MAX_OTHER_CAMERAS);
    const errorNames = [];
    for (const cameraDeviceId of candidates) {
        if (!isCurrent()) {
            break;
        }
        try {
            const tracks = await createVideoTrack(cameraDeviceId);
            emit('camera_other_camera_fallback', {
                ...report,
                // matchedRequest: whether the camera that opened is the
                // candidate asked for (requestedCameraRef is the one that failed).
                ...describeObtainedCamera(tracks, cameraDeviceId),
                attempts: errorNames.length + 1,
                candidates: candidates.length,
                error_names: errorNames,
                outcome: 'acquired'
            });
            // Never switch cameras silently: the settings still name the
            // chosen camera, so say which one the call is using and why.
            const labelOf = (id) => devices.find(device => device.deviceId === id)?.label || undefined;
            notify({
                obtainedLabel: tracks?.[0]?.getTrack?.()?.label || labelOf(cameraDeviceId),
                reason: failureName === 'NotReadableError' || failureName === 'AbortError' ? 'busy' : 'missing',
                requestedLabel: labelOf(preferredDeviceId)
            });
            return tracks;
        }
        catch (error) {
            errorNames.push(getMediaErrorName(error));
        }
    }
    emit('camera_other_camera_fallback', {
        ...report,
        attempts: errorNames.length,
        candidates: candidates.length,
        error_names: errorNames,
        outcome: candidates.length ? 'all_failed' : 'no_other_camera'
    });
    throw failure;
}
